import { createTheme } from "@mui/material/styles";

const lightPurpleHover = "#B977FF";
const darkpurpleDefault = "#5A2891";

const theme = createTheme({
  // -------------------------------------------------------
  // 1. Palette
  // -------------------------------------------------------
  palette: {
    primary: {
      main: "#5A2891",
      dark: "#025d74",
      light: "#f9e0ff",
      figma: "#24034F",
      LPtitles: "#000000"
    },
    secondary: {
      main: "#ffffff",
      pillColor: "#9946DF",
      LPMapSection: "#F1E4FF",
      figma: "#B977FF",
    },
    accent: {
      main: "#FFBC22",
      followUp: "#0b53d9",
      followUpLight: "#4a84f0",
      followUpUltraLight: "#89d6f9",
      followUpWhite: "#d3f1ff",
      followUpUltraWhite: "#f2fafe"
    },
    background: {
      default: "#F9EBFF",
      paper: "#ffffff",
      figma: "#F3D9FF",
      slate: "#FAF5FF"
    },
    text: {
      primary: "#282828",
      secondary: "#404040",
      tertiary: "#757575",
      disabled: "#9e9e9e",
    },

    // 2. Colors in Single Program Page 2
    COLORS: {
      option1: "#FFB74D",
      option2: "#4FC3F7",
      option3: "#81C784",
      option4: "#BA68C8",
      option5: "#E57373"
    },

    tileColors: {
      foundational: "#d6ffda",
      inception: "#d3edff",
      infusion: "#ffeccc",
      volunteerDevelopment: "#ecc1ff",
      national: "#ffc1d6"
    },

    tileColorsHover: {
      foundational: "#beffc4",
      inception: "#c5e7ff",
      infusion: "#ffe5b9",
      volunteerDevelopment: "#ebbbff",
      national: "#ffb5ce",
    }
  },

  // -------------------------------------------------------
  // 2. Typography (Updated with your fonts)
  // -------------------------------------------------------
  typography: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",

    h1: { fontSize: "2.5rem", fontWeight: 700 },
    h2: { fontSize: "2rem", fontWeight: 600 },
    h3: { fontSize: "1.75rem", fontWeight: 600 },
    h4: { fontSize: "1.5rem", fontWeight: 500 },
    h5: { fontSize: "1.25rem", fontWeight: 500 },
    h6: { fontSize: "1rem", fontWeight: 500 },

    body1: { fontSize: "1rem", lineHeight: 1.5 },
    body2: { fontSize: "0.875rem", lineHeight: 1.4 },

    button: { textTransform: "none", fontWeight: 600 },

    caption: { fontSize: "0.75rem" },
    overline: { fontSize: "0.75rem", letterSpacing: 1 },

    // ⭐ Custom variant for numbers
    number: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 700,
    },
  },

  // -------------------------------------------------------
  // 3. Spacing
  // -------------------------------------------------------
  spacing: 8,

  // -------------------------------------------------------
  // 4. Breakpoints
  // -------------------------------------------------------
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
    },
  },

  // -------------------------------------------------------
  // 6. Shadows
  // -------------------------------------------------------
  shadows: [
    "none",
    "0px 1px 3px rgba(0,0,0,0.2)",
    "0px 1px 5px rgba(0,0,0,0.2)",
  ],

  // -------------------------------------------------------
  // 7. Z-index
  // -------------------------------------------------------
  zIndex: {
    appBar: 1200,
    drawer: 1100,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },

  // -------------------------------------------------------
  // 8. Components Overrides
  // -------------------------------------------------------
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
          "&:hover": {
            backgroundColor: lightPurpleHover,
            transition: "background-color 0.2s ease-in-out",
            color: "white",
          },
          "&.Mui-focusVisible": {
            backgroundColor: lightPurpleHover,
          },
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "customPink" },
          style: {
            backgroundColor: "#F8BBD0",
            color: "#000",
            "&:hover": {
              backgroundColor: "#f48fb1",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "outlined", color: "customPink" },
          style: {
            borderColor: "#F8BBD0",
            color: "#000",
            "&:hover": {
              backgroundColor: "#F8BBD0",
              color: "#000",
              borderColor: "#F8BBD0",
            },
          },
        },
      ],
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16, // Rounded corners
          boxShadow: '0 4px 12px rgba(20,30,50,0.10)', // Soft shadow
          padding: '8px 0',
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          border: "1px solid rgba(120,145,160,0.25)",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(20,30,50,0.10)",
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        pageBg: {
          padding: "24px",
          minHeight: "100vh",
          // overflow: "auto",
          backgroundColor: "#FFFFFF", // #F9EBFF
          // backgroundColor: "#F5FAFE",
          border: "1px solid #e0e0e0",
          boxShadow: "0 0px 8px rgba(90,40,145,0.08)"
        }
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#027b9a",
        },
      },
    },

    // Typography Overrides
    MuiTypography: {
      styleOverrides: {
        root: {
          marginBottom: "4px",
        },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        h1: { fontFamily: "'Plus Jakarta Sans'" },
        h2: { fontFamily: "'Plus Jakarta Sans'" },
        h3: { fontFamily: "'Plus Jakarta Sans'" },
        h4: { fontFamily: "'Plus Jakarta Sans'" },
        h5: { fontFamily: "'Plus Jakarta Sans'" },
        h6: { fontFamily: "'Plus Jakarta Sans'" },
        p: { fontFamily: "'Plus Jakarta Sans'" },
        small: { fontFamily: "'Plus Jakarta Sans'" },
      },
    },
  },




 
  committeeStyles: {
    "Governing Committee": {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #F6FFE7 100%)",
      solid: "#F6FFE7",
    },
    "Operations Committee" : {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #E7F6FF 100%)",
      solid: "#E7F6FF",
    },
     "Operation Committee" : {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #E7F6FF 100%)",
      solid: "#E7F6FF",
    },
    "National Program Head": {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #FFF7D8 100%)",
      solid: "#FFF7D8",
    },

    "Program Head": {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #FFF7D8 100%)",
      solid: "#FFF7D8",
    },

     "State Program Heads": {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #FFF7D8 100%)",
      solid: "#FFF7D8",
    },

    
    "State Presidents & State General Secretaries": {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #FFDABB 100%)",
      solid: "#FFDABB",
    },


     "Other Members": {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #FFDABB 100%)",
      solid: "#FFDABB",
    },
    // fallback
    default: {
      gradient: "linear-gradient(180deg, #FFFFFF 0%, #F5F5F5 100%)",
      solid: "#F5F5F5",
    },
  },




});

export default theme;
